import React from 'react';
import { Loader2, ArrowDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PullToRefreshIndicatorProps {
  isPulling: boolean;
  isRefreshing: boolean;
  pullProgress: number;
}

const PullToRefreshIndicator: React.FC<PullToRefreshIndicatorProps> = ({
  isPulling,
  isRefreshing,
  pullProgress
}) => {
  if (!isPulling && !isRefreshing) return null;

  const opacity = Math.min(pullProgress, 1);
  const scale = 0.5 + Math.min(pullProgress, 1) * 0.5;
  const translateY = Math.min(pullProgress * 40, 50);
  const isReady = pullProgress >= 1;

  return (
    <div 
      className="absolute left-0 right-0 flex justify-center pointer-events-none z-10"
      style={{ 
        top: 0,
        transform: `translateY(${translateY}px)`,
        opacity: isRefreshing ? 1 : opacity,
        transition: isRefreshing ? 'none' : 'transform 0.1s ease-out'
      }}
      role="status"
      aria-live="polite"
    >
      <div 
        className={cn(
          "w-10 h-10 rounded-full bg-background shadow-lg border-2 flex items-center justify-center",
          isReady ? "border-primary" : "border-primary/20"
        )}
        style={{ 
          transform: `scale(${isRefreshing ? 1 : scale})`,
          transition: isRefreshing ? 'none' : 'transform 0.1s ease-out'
        }}
      >
        {isRefreshing ? (
          <Loader2 className="w-5 h-5 text-primary animate-spin" aria-hidden="true" />
        ) : (
          <ArrowDown 
            className={cn(
              "w-5 h-5 text-primary transition-transform duration-200",
              isReady && "rotate-180"
            )}
            style={{ 
              transform: isReady ? 'rotate(180deg)' : 'none'
            }}
            aria-hidden="true"
          />
        )}
      </div>
      <span className="sr-only">
        {isRefreshing ? 'Refreshing...' : isReady ? 'Release to refresh' : 'Pull to refresh'}
      </span>
    </div>
  );
};

export default PullToRefreshIndicator;
